import api from 'api/api.config';
import { buildQueryParams } from 'helpers/api/build-query-params';
import type {
  SortDirection,
  FilterValue,
} from 'helpers/api/build-query-params';

import type { CollectionResponse } from 'api/market-view-api';

const getModelFundsPath = (
  userId: string,
  modelId: string | number,
): string => `/${userId}/aid-model/${modelId}/aid-special-funds`;

const getClientFundsPath = (
  userId: string,
  clientId: string | number,
): string => `/${userId}/clients/${clientId}/aid-special-funds`;

export const getModelSpecialFunds = async (
  userId: string,
  modelId: string | number,
): Promise<unknown> => {
  const response = await api.get(getModelFundsPath(userId, modelId));

  return response.data;
};

export interface ClientSpecialFundsParams {
  pageSize?: number;
  pageIndex?: number;
  sortBy?: SortDirection[];
  filters?: Record<string, FilterValue> | null;
  modelId?: string | number;
}

export const getClientSpecialFunds = async (
  userId: string,
  clientId: string | number,
  params: ClientSpecialFundsParams = {},
): Promise<CollectionResponse> => {
  const { pageSize, pageIndex = 0, sortBy, filters, modelId } = params;

  const queryParams = buildQueryParams({
    pageSize,
    pageIndex,
    sortBy,
    filters,
  });

  if (modelId) {
    queryParams.append('modelId', String(modelId));
  }

  const response = await api.get(getClientFundsPath(userId, clientId), {
    params: queryParams,
  });

  const totalCount = response.headers['x-total-count'];
  const pageSizeHeader = response.headers['x-page-size'];
  const pageCount = response.headers['x-page-count'];

  const numPages = Math.ceil(
    Number.parseInt(totalCount, 10) /
      Number.parseInt(String(pageSizeHeader || pageSize || '10'), 10),
  );

  return {
    data: response.data,
    pagination: {
      page: Number.parseInt(pageCount, 10) || pageIndex,
      pageSize: Number.parseInt(pageSizeHeader, 10) || pageSize || 10,
      pageCount: numPages,
      totalRecords: Number.parseInt(totalCount, 10) || 0,
    },
  };
};

export const createModelSpecialFund = async (
  userId: string,
  modelId: string | number,
  data: unknown,
): Promise<unknown> => {
  const response = await api.post(
    getModelFundsPath(userId, modelId),
    JSON.stringify(data),
  );

  return response.data;
};

export const updateModelSpecialFund = async (
  userId: string,
  modelId: string | number,
  fundId: string | number,
  data: unknown,
): Promise<unknown> => {
  const response = await api.put(
    `${getModelFundsPath(userId, modelId)}/${fundId}`,
    JSON.stringify(data),
  );

  return response.data;
};

export const deleteModelSpecialFund = async (
  userId: string,
  modelId: string | number,
  fundId: string | number,
): Promise<void> => {
  await api.delete(`${getModelFundsPath(userId, modelId)}/${fundId}`);
};
